/**
 * Format dates/times in the viewer's timezone for consistent display.
 * Use these instead of toLocaleDateString() / toLocaleString() so everyone sees correct values in their timezone.
 */

import { getIANATimezone, getTimezoneAbbreviation } from './timezoneUtils';

function getIanaOffsetMinutes(timeZone, utcDate) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(utcDate);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const y = Number(get('year'));
  const m = Number(get('month'));
  const d = Number(get('day'));
  const hh = Number(get('hour'));
  const mm = Number(get('minute'));
  const ss = Number(get('second'));
  const asUTC = Date.UTC(y, m - 1, d, hh === 24 ? 0 : hh, mm, ss);
  return (asUTC - utcDate.getTime()) / 60000;
}

/**
 * Interpret a wall-clock date/time in the given timezone (abbrev or IANA) as a UTC Date.
 * Avoids `new Date('YYYY-MM-DDTHH:mm')`, which uses the browser's local timezone.
 */
export function zonedWallTimeToUtcDate(dateStrYYYYMMDD, timeStr, timezone) {
  if (!dateStrYYYYMMDD || !timeStr) return null;
  const iana = getIANATimezone(timezone) || (typeof timezone === 'string' && timezone.includes('/') ? timezone : 'UTC');
  const dateStr = String(dateStrYYYYMMDD).slice(0, 10);
  const timePart = String(timeStr).trim();
  const normalizedTime = timePart.length <= 5 ? `${timePart}:00` : timePart.slice(0, 8);
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm, ss] = normalizedTime.split(':').map(Number);
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;

  let utcMs = Date.UTC(y, m - 1, d, hh, mm, ss || 0);
  // Two-pass offset correction so DST boundaries are handled more reliably.
  for (let i = 0; i < 2; i += 1) {
    const offsetMin = getIanaOffsetMinutes(iana, new Date(utcMs));
    utcMs = Date.UTC(y, m - 1, d, hh, mm, ss || 0) - offsetMin * 60000;
  }
  return new Date(utcMs);
}

/**
 * Preview for schedule forms: entered wall time in the profile timezone + IST conversion.
 * @returns {{ timezoneAbbr: string, entered: string, ist: string } | null}
 */
export function formatEnteredSchedulePreview(dateInput, timeInput, timezone) {
  if (!dateInput || !timeInput) return null;
  try {
    const dateStr = typeof dateInput === 'string'
      ? dateInput.slice(0, 10)
      : (dateInput?.format
        ? dateInput.format('YYYY-MM-DD')
        : (dateInput instanceof Date
          ? `${dateInput.getFullYear()}-${String(dateInput.getMonth() + 1).padStart(2, '0')}-${String(dateInput.getDate()).padStart(2, '0')}`
          : null));
    if (!dateStr) return null;
    const timeStr = String(timeInput).length > 5 ? String(timeInput).slice(0, 5) : String(timeInput);
    const utcDate = zonedWallTimeToUtcDate(dateStr, timeStr, timezone);
    if (!utcDate || Number.isNaN(utcDate.getTime())) return null;

    const iana = getIANATimezone(timezone) || (typeof timezone === 'string' && timezone.includes('/') ? timezone : 'UTC');
    const timezoneAbbr = getTimezoneAbbreviation(timezone || iana);
    const entered = new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: iana,
    }).format(utcDate);
    const ist = new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Asia/Kolkata',
    }).format(utcDate);
    return { timezoneAbbr, entered, ist };
  } catch {
    return null;
  }
}

const COUNTRY_DISPLAY_ZONES = {
  USA: ['EST', 'CST', 'MST', 'PST'],
  'United States': ['EST', 'CST', 'MST', 'PST'],
  Canada: ['EST', 'CST', 'MST', 'PST'],
  Australia: ['AEST', 'AEST_QLD', 'ACST', 'AWST'],
  UK: ['GMT', 'BST'],
  'United Kingdom': ['GMT', 'BST'],
  'New Zealand': ['NZST'],
  India: ['IST'],
  Singapore: ['SGT'],
  Japan: ['JST'],
  UAE: ['GST'],
  Kuwait: ['GST'],
  Oman: ['GST'],
  'Saudi Arabia': ['GST'],
  Germany: ['CET'],
  Netherlands: ['CET'],
  Sweden: ['CET'],
  Indonesia: ['WIB', 'WITA', 'WIT'],
};

/** UK date uses London; GMT/BST clocks are always both UTC and UTC+1 (not DST-switched labels). */
const COUNTRY_DATE_IANA = {
  UK: 'Europe/London',
  'United Kingdom': 'Europe/London',
  'New Zealand': 'Pacific/Auckland',
};

function getCountryClockIana(abbr) {
  const key = String(abbr || '').trim();
  if (key === 'GMT') return 'UTC';
  if (key === 'BST') return 'Etc/GMT-1';
  return getIANATimezone(key) || (key.includes('/') ? key : 'UTC');
}

function getCountryDateIana(country, firstAbbr) {
  if (COUNTRY_DATE_IANA[country]) return COUNTRY_DATE_IANA[country];
  return getCountryClockIana(firstAbbr);
}

const CLOCK_ABBR_LABEL = {
  AEST_QLD: 'AEST (Brisbane)',
};

/** US clocks use New York / Chicago / Denver / Los Angeles, so the letters follow that date (EDT in summer, EST in winter). */
const US_CLOCK_ABBRS = new Set(['EST', 'CST', 'MST', 'PST']);

function usClockLabel(utcDate, iana, fallback) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      timeZoneName: 'short',
    }).formatToParts(utcDate);
    const name = parts.find((part) => part.type === 'timeZoneName')?.value || '';
    if (/^[A-Z]{2,5}$/.test(name)) return name;
  } catch {
    /* keep EST/CST/MST/PST */
  }
  return fallback;
}

function formatClockInZone(utcDate, abbr) {
  const iana = getCountryClockIana(abbr);
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: iana,
  }).format(utcDate);
  const label = US_CLOCK_ABBRS.has(abbr)
    ? usClockLabel(utcDate, iana, abbr)
    : (CLOCK_ABBR_LABEL[abbr] || abbr);
  return `${time} ${label}`;
}

function formatDateInIana(utcDate, iana) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: iana,
  }).format(utcDate);
}

export function buildDemoCountryScheduleRows(utcDate, timezone, countries = []) {
  if (!utcDate || Number.isNaN(utcDate.getTime())) return [];
  const timezoneAbbr = getTimezoneAbbreviation(timezone || '');
  return (Array.isArray(countries) ? countries : []).filter(Boolean).map((country) => {
    const abbrevs = COUNTRY_DISPLAY_ZONES[country]?.length
      ? COUNTRY_DISPLAY_ZONES[country]
      : [timezoneAbbr || 'UTC'];
    const dateLine = formatDateInIana(utcDate, getCountryDateIana(country, abbrevs[0]));
    const timeLine = abbrevs.map((abbr) => formatClockInZone(utcDate, abbr)).join(' / ');
    return {
      country,
      dateLine,
      timeLine,
      line: `${dateLine} · ${timeLine}`,
    };
  });
}

export function getDemoUtcDate(demo) {
  if (!demo) return null;
  if (demo.scheduled_at_iso) {
    const date = new Date(demo.scheduled_at_iso);
    if (!Number.isNaN(date.getTime())) return date;
  }
  if (demo.scheduled_date && demo.scheduled_time) {
    return zonedWallTimeToUtcDate(demo.scheduled_date, demo.scheduled_time, demo.scheduled_timezone || 'UTC');
  }
  return null;
}

export function formatDemoCountriesFromDemo(demo) {
  const utcDate = getDemoUtcDate(demo);
  if (!utcDate) return [];
  return buildDemoCountryScheduleRows(utcDate, demo.scheduled_timezone, demo.countries);
}

export function isIstTimezone(timezone) {
  const raw = String(timezone || '').trim();
  if (!raw) return false;
  if (raw.toUpperCase() === 'IST') return true;
  const iana = getIANATimezone(raw) || raw;
  return iana === 'Asia/Kolkata' || iana === 'Asia/Calcutta';
}

/** IST date/time for ops, independent of selected demo countries. */
export function formatDemoIstSchedule(demo) {
  const utcDate = getDemoUtcDate(demo);
  if (!utcDate) return null;
  const dateLine = formatDateInIana(utcDate, 'Asia/Kolkata');
  const timeLine = formatClockInZone(utcDate, 'IST');
  return {
    country: 'IST',
    dateLine,
    timeLine,
    line: `${dateLine} · ${timeLine}`,
  };
}

function demoCountriesIncludeIndia(demo) {
  return (Array.isArray(demo?.countries) ? demo.countries : []).some(
    (country) => String(country).trim().toLowerCase() === 'india'
  );
}

/**
 * Country clocks plus an optional IST line for the demo list.
 * Skip IST when India is already selected (that row is already IST).
 * When there are no country rows, skip IST if the viewer is already on IST
 * (the viewer fallback is the same clock).
 */
export function getDemoListScheduleDisplay(demo, viewerTimezone) {
  const countryRows = formatDemoCountriesFromDemo(demo);
  const istRow = formatDemoIstSchedule(demo);
  const hideIst = !istRow
    || demoCountriesIncludeIndia(demo)
    || (!countryRows.length && isIstTimezone(viewerTimezone));
  return {
    countryRows,
    istRow: hideIst ? null : istRow,
  };
}

export function formatDemoListScheduleRows(demo, viewerTimezone) {
  const { countryRows, istRow } = getDemoListScheduleDisplay(demo, viewerTimezone);
  return istRow ? [...countryRows, istRow] : countryRows;
}

/**
 * Convert an entered wall-clock (in `timezone`) into slash-separated local times
 * for each selected demo country (USA EDT/EST and the other US clocks, UK GMT/BST, …).
 * @returns {{ timezoneAbbr: string, entered: string, countries: Array<{ country: string, dateLine: string, timeLine: string, line: string }> } | null}
 */
export function formatDemoCountriesSchedulePreview(dateInput, timeInput, timezone, countries = []) {
  if (!dateInput || !timeInput) return null;
  try {
    const dateStr = typeof dateInput === 'string'
      ? dateInput.slice(0, 10)
      : (dateInput?.format
        ? dateInput.format('YYYY-MM-DD')
        : (dateInput instanceof Date
          ? `${dateInput.getFullYear()}-${String(dateInput.getMonth() + 1).padStart(2, '0')}-${String(dateInput.getDate()).padStart(2, '0')}`
          : null));
    if (!dateStr) return null;
    const timeStr = String(timeInput).length > 5 ? String(timeInput).slice(0, 5) : String(timeInput);
    const utcDate = zonedWallTimeToUtcDate(dateStr, timeStr, timezone);
    if (!utcDate || Number.isNaN(utcDate.getTime())) return null;

    const iana = getIANATimezone(timezone) || (typeof timezone === 'string' && timezone.includes('/') ? timezone : 'UTC');
    const timezoneAbbr = getTimezoneAbbreviation(timezone || iana);
    const entered = new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: iana,
    }).format(utcDate);

    return {
      timezoneAbbr,
      entered,
      countries: buildDemoCountryScheduleRows(utcDate, timezone, countries),
    };
  } catch {
    return null;
  }
}

/**
 * Get viewer timezone: user's stored timezone or browser timezone.
 * @param {string} [userTimezone] - From user profile (e.g. user?.timezone)
 * @returns {string} IANA timezone (e.g. 'America/New_York', 'Asia/Kolkata')
 */
export function getViewerTimezone(userTimezone) {
  if (userTimezone && typeof userTimezone === 'string' && userTimezone.trim()) {
    const raw = userTimezone.trim();
    return getIANATimezone(raw) || raw;
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Calendar YYYY-MM-DD of a demo instant in the viewer's timezone.
 */
export function getDemoCalendarYmdInTimezone(demo, viewerTimezone) {
  if (!demo) return '';
  if (demo.scheduled_at_iso) {
    const tz = getViewerTimezone(viewerTimezone);
    try {
      const date = new Date(demo.scheduled_at_iso);
      if (Number.isNaN(date.getTime())) return String(demo.scheduled_date || '').slice(0, 10);
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    } catch {
      return String(demo.scheduled_date || '').slice(0, 10);
    }
  }
  return String(demo.scheduled_date || '').slice(0, 10);
}

export function formatDemoDateInViewerTZ(demo, viewerTimezone) {
  if (!demo) return '—';
  if (demo.scheduled_at_iso) {
    return formatDateInViewerTZ(demo.scheduled_at_iso, viewerTimezone) || '—';
  }
  return formatCalendarDate(demo.scheduled_date);
}

export function formatDemoTimeInViewerTZ(demo, viewerTimezone) {
  if (!demo) return '—';
  if (demo.scheduled_at_iso) {
    return formatTimeInViewerTZ(demo.scheduled_at_iso, viewerTimezone) || '—';
  }
  return formatTimeOnly(demo.scheduled_time) || '—';
}

export function formatDemoDateTimeInViewerTZ(demo, viewerTimezone) {
  if (!demo) return '—';
  if (demo.scheduled_at_iso) {
    return formatDateTimeInViewerTZ(demo.scheduled_at_iso, viewerTimezone) || '—';
  }
  const d = formatCalendarDate(demo.scheduled_date);
  const t = formatTimeOnly(demo.scheduled_time);
  return t ? `${d}, ${t}` : d;
}

/**
 * Calendar date (YYYY-MM-DD) for "now" in the given timezone (matches session_date storage).
 */
export function getTodayYMDInTimezone(timezone) {
  const tz = getViewerTimezone(timezone);
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

/** Monday of the Mon–Sun week for a civil YYYY-MM-DD (UTC date math, no laptop TZ). */
export function mondayOfCalendarYmd(ymd) {
  const s = String(ymd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(`${s}T00:00:00.000Z`);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

/** Monday of the current week in the given IANA/abbrev timezone. */
export function getWeekStartInTimezone(timezone) {
  return mondayOfCalendarYmd(getTodayYMDInTimezone(timezone));
}

export function localDateFromYmd(ymd) {
  const s = String(ymd || '').slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d);
}

/** UTC/ISO instant → naive YYYY-MM-DDTHH:mm:ss in timezone (FullCalendar local wall clock). */
export function isoToNaiveInTimezone(isoString, timezone) {
  if (!isoString) return null;
  const parts = getDateAndTimeInTimezone(isoString, timezone);
  if (!parts?.dateStr || !parts?.timeStr) return null;
  const time = parts.timeStr.length === 5 ? `${parts.timeStr}:00` : parts.timeStr.slice(0, 8);
  return `${parts.dateStr}T${time}`;
}

export function isSessionDateInFuture(sessionDateYMD, timezone) {
  if (!sessionDateYMD) return false;
  return String(sessionDateYMD).slice(0, 10) > getTodayYMDInTimezone(timezone);
}

/**
 * Format an ISO 8601 date-time string in the viewer's timezone.
 * @param {string} isoString - UTC ISO string (e.g. from scheduled_at_iso)
 * @param {string} [viewerTimezone] - Viewer's timezone; defaults to browser timezone
 * @param {{ dateStyle?: string, timeStyle?: string }} [options] - Intl options
 * @returns {string} Formatted string
 */
export function formatDateTimeInViewerTZ(isoString, viewerTimezone, options = {}) {
  if (!isoString) return '';
  const tz = getViewerTimezone(viewerTimezone);
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const { dateStyle = 'medium', timeStyle = 'short' } = options;
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      dateStyle,
      timeStyle
    }).format(date);
  } catch {
    return '';
  }
}

/**
 * Format date only (e.g. for scheduled_date, lead_acquired_at, batch start_date).
 * @param {string|Date} dateInput - ISO string or Date
 * @param {string} [viewerTimezone] - Viewer's timezone
 * @returns {string}
 */
export function formatDateInViewerTZ(dateInput, viewerTimezone) {
  if (!dateInput) return '';
  const tz = getViewerTimezone(viewerTimezone);
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      dateStyle: 'medium'
    }).format(date);
  } catch {
    return '';
  }
}

/**
 * Format time only from an ISO string, in viewer's timezone.
 */
export function formatTimeInViewerTZ(isoString, viewerTimezone) {
  if (!isoString) return '';
  const tz = getViewerTimezone(viewerTimezone);
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, { timeZone: tz, timeStyle: 'short' }).format(date);
  } catch {
    return '';
  }
}

/**
 * Get date (YYYY-MM-DD) and time (HH:mm) in a given timezone from an ISO string.
 * Use to initialize forms from scheduled_at_iso so the pre-filled date/time match the user's timezone display.
 * @param {string} isoString - UTC ISO string (e.g. scheduled_at_iso)
 * @param {string} [timezone] - IANA timezone (defaults to viewer/browser)
 * @returns {{ dateStr: string, timeStr: string } | null}
 */
export function getDateAndTimeInTimezone(isoString, timezone) {
  if (!isoString) return null;
  const tz = getViewerTimezone(timezone);
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return null;
    const dateStr = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
    const timeStr = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
    return { dateStr, timeStr };
  } catch {
    return null;
  }
}

/**
 * Format time only from HH:mm:ss string (no timezone; use when scheduled_at_iso not available).
 */
export function formatTimeOnly(timeStr) {
  if (!timeStr) return '';
  const part = String(timeStr).trim().slice(0, 8);
  const [h, m] = part.split(':').map(Number);
  if (isNaN(h)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  const min = (m != null && !isNaN(m)) ? m : 0;
  return `${hour}:${String(min).padStart(2, '0')} ${period}`;
}

// --- Demo/trial scheduled date-time: always use STORED timezone so list/form/details never mismatch ---

const CALENDAR_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CALENDAR_YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalize a DATE/DOB value to YYYY-MM-DD without UTC shifting.
 * Date-only strings are kept as-is. Never use toISOString() for this.
 */
export function toCalendarYmd(value) {
  if (value == null || value === '') return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  if (CALENDAR_YMD_RE.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}[T ]/.test(s)) {
    const dt = new Date(s);
    if (!Number.isNaN(dt.getTime())) {
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const d = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }
  const ymd = s.slice(0, 10);
  return CALENDAR_YMD_RE.test(ymd) ? ymd : '';
}

export function calculateAgeFromCalendarYmd(value, today = new Date()) {
  const ymd = toCalendarYmd(value);
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  let age = today.getFullYear() - y;
  const monthDiff = today.getMonth() + 1 - m;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d)) age -= 1;
  return age >= 0 && age <= 120 ? age : null;
}

/**
 * Format YYYY-MM-DD as calendar date only (no timezone conversion). Use for stored scheduled_date
 * so the displayed date never shifts to another day (avoids list showing 8 Mar when form shows 9 Mar).
 * @param {string} dateStr - YYYY-MM-DD
 * @returns {string} e.g. "9 Mar 2026"
 */
export function formatCalendarDate(dateStr) {
  const s = toCalendarYmd(dateStr);
  if (!s) return '—';
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  const month = CALENDAR_MONTHS[m - 1];
  return month ? `${d} ${month} ${y}` : s;
}

/** Student-local date, plus teacher date when they differ (overnight US/India classes). */
export function formatAttendanceSessionDate(session) {
  const local = toCalendarYmd(session?.localDate || session?.sessionDate);
  const teacher = toCalendarYmd(session?.teacherDate || session?.sessionDate);
  if (local && teacher && local !== teacher) {
    return `${formatCalendarDate(local)} (student) · ${formatCalendarDate(teacher)} (teacher)`;
  }
  return formatCalendarDate(local || teacher);
}

/**
 * Format demo scheduled DATE in stored timezone (or calendar date fallback). Use everywhere we display
 * demo date so list, details, and forms show the same calendar date.
 * @param {{ scheduled_at_iso?: string, scheduled_date?: string, scheduled_timezone?: string }} demo
 * @returns {string}
 */
export function formatDemoDateStored(demo) {
  if (!demo) return '—';
  const tz = demo.scheduled_timezone || 'UTC';
  if (demo.scheduled_at_iso) {
    return formatDateInViewerTZ(demo.scheduled_at_iso, tz);
  }
  return formatCalendarDate(demo.scheduled_date);
}

/**
 * Format demo scheduled TIME in stored timezone (or time string fallback). Use with formatDemoDateStored
 * so date and time match what's stored and what forms show.
 * @param {{ scheduled_at_iso?: string, scheduled_time?: string, scheduled_timezone?: string }} demo
 * @returns {string}
 */
export function formatDemoTimeStored(demo) {
  if (!demo) return '—';
  const tz = demo.scheduled_timezone || 'UTC';
  if (demo.scheduled_at_iso) {
    return formatTimeInViewerTZ(demo.scheduled_at_iso, tz);
  }
  return formatTimeOnly(demo.scheduled_time) || '—';
}

/**
 * Format demo scheduled date and time in stored timezone. Use for lists and anywhere we need to show
 * the same date/time as the stored value (no viewer-TZ shift that could change the calendar date).
 * @param {{ scheduled_at_iso?: string, scheduled_date?: string, scheduled_time?: string, scheduled_timezone?: string }} demo
 * @returns {string} e.g. "9 Mar 2026, 3:30 AM"
 */
export function formatDemoDateTimeStored(demo) {
  if (!demo) return '—';
  const tz = demo.scheduled_timezone || 'UTC';
  if (demo.scheduled_at_iso) {
    const datePart = formatDateInViewerTZ(demo.scheduled_at_iso, tz);
    const timePart = formatTimeInViewerTZ(demo.scheduled_at_iso, tz);
    return datePart && timePart ? `${datePart}, ${timePart}` : datePart || timePart || '—';
  }
  const d = formatCalendarDate(demo.scheduled_date);
  const t = formatTimeOnly(demo.scheduled_time);
  return t ? `${d}, ${t}` : d;
}

/** Country clocks for compact captions (no “stored vs your timezone”). */
export function formatDemoDualCaption(demo, viewerTimezone) {
  const rows = formatDemoCountriesFromDemo(demo);
  if (rows.length) {
    return rows.map((row) => `${row.country}: ${row.line}`).join(' · ');
  }
  return formatDemoDateTimeInViewerTZ(demo, viewerTimezone);
}

/**
 * Format a Date as local calendar YYYY-MM-DD (no UTC shift via toISOString).
 * Use for dashboard date filters in the viewer's local timezone.
 * @param {Date} [date]
 * @returns {string}
 */
export function formatLocalYMD(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * First calendar day of the month for a local date, as YYYY-MM-DD.
 * @param {Date} [date]
 * @returns {string}
 */
export function getLocalMonthStartYMD(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  return formatLocalYMD(new Date(d.getFullYear(), d.getMonth(), 1));
}

/**
 * Shift a local calendar date by N days and return YYYY-MM-DD.
 * @param {number} days
 * @param {Date} [date]
 * @returns {string}
 */
export function shiftLocalYMD(days, date = new Date()) {
  const d = date instanceof Date ? new Date(date.getTime()) : new Date(date);
  d.setDate(d.getDate() + days);
  return formatLocalYMD(d);
}
